import React, { useEffect, useState, useRef } from 'react';
import { ShoppingCart } from 'lucide-react';
import { getShopifySettings, getShopifyUI } from '../lib/shopifyClient';

/**
 * ShopifyCartToggle — custom navbar cart icon that opens the Shopify
 * mini-cart drawer and shows a live item count.
 *
 * Implementation note: the Shopify Buy Button SDK auto-renders its OWN
 * floating cart toggle (bottom-right corner) the first time a product
 * component is created. Rather than fight the SDK internals to read the
 * shared cart state (which is unreliable across versions), we:
 *
 *   1. Read the count directly from the auto-rendered toggle iframe's
 *      `.shopify-buy__cart-toggle__count` text node. The iframe uses
 *      `srcdoc` so it's same-origin and freely readable from our JS.
 *   2. Open the drawer by clicking the toggle iframe's button.
 *   3. Hide the floating toggle visually (CSS) so users only see our
 *      themed icon in the navbar, but the SDK keeps managing it under the
 *      hood as a stable, well-tested cart trigger.
 */

const findFloatingToggleIframe = () => {
  // The toggle iframe has no class on the <iframe> element itself, but its
  // inner body contains `shopify-buy__cart-toggle`. Walk all iframes once.
  const frames = Array.from(document.querySelectorAll('iframe'));
  for (const f of frames) {
    try {
      const doc = f.contentDocument;
      if (doc && doc.querySelector('.shopify-buy__cart-toggle')) return f;
    } catch (_) { /* cross-origin — skip */ }
  }
  return null;
};

const readCountFromToggle = () => {
  const iframe = findFloatingToggleIframe();
  if (!iframe || !iframe.contentDocument) return 0;
  const el = iframe.contentDocument.querySelector('.shopify-buy__cart-toggle__count');
  if (!el) return 0;
  const n = parseInt((el.textContent || '0').trim(), 10);
  return Number.isFinite(n) ? n : 0;
};

const openCartViaToggle = () => {
  const iframe = findFloatingToggleIframe();
  if (!iframe || !iframe.contentDocument) return false;
  // Briefly un-hide so the click reaches the toggle button, then re-hide.
  const wasHidden = iframe.style.display === 'none';
  if (wasHidden) { iframe.style.display = ''; iframe.style.visibility = ''; }
  const btn =
    iframe.contentDocument.querySelector('.shopify-buy__cart-toggle') ||
    iframe.contentDocument.querySelector('button');
  let ok = false;
  if (btn) {
    btn.click();
    ok = true;
  }
  // Re-hide on the next tick — the drawer is a different iframe so hiding
  // the toggle again won't affect the now-open cart.
  if (wasHidden) setTimeout(() => { iframe.style.display = 'none'; iframe.style.visibility = 'hidden'; }, 100);
  return ok;
};

let _polling = false;
const _countListeners = new Set();

const broadcast = () => {
  const n = readCountFromToggle();
  _countListeners.forEach((fn) => { try { fn(n); } catch (_) { /* ignore */ } });
};

const startPolling = () => {
  if (_polling) return;
  _polling = true;
  // Light poll. Each tick is just a DOM text read so it's basically free.
  // We tick fast (every 500ms) for the first 60s after mount so the badge
  // updates promptly when a fan first adds an item, then back off to 1.5s.
  let ticks = 0;
  const fast = setInterval(() => {
    broadcast();
    ticks += 1;
    if (ticks > 120) {
      clearInterval(fast);
      setInterval(broadcast, 1500);
    }
  }, 500);
};

// CSS injected once to hide the SDK's floating cart toggle. We keep its
// drawer iframe interactive (that's the actual mini-cart) — we only hide the
// floating circular button at the bottom-right corner of the page.
const injectHideCSS = () => {
  if (document.getElementById('shopify-cart-toggle-hide')) return;
  const style = document.createElement('style');
  style.id = 'shopify-cart-toggle-hide';
  style.textContent = `
    /* Hide Shopify's auto-rendered floating cart toggle button so only
       our themed navbar icon is visible. We DON'T touch the drawer
       (which is a separate iframe). The toggle's wrapper has class
       'shopify-buy-frame--toggle' in current SDK versions; older versions
       may not — see hideFloatingToggle() for the JS-based fallback. */
    iframe.shopify-buy-frame--toggle,
    .shopify-buy-frame--toggle,
    div.shopify-buy-frame--toggle {
      display: none !important;
      visibility: hidden !important;
    }
  `;
  document.head.appendChild(style);
};

// Belt-and-suspenders: walk iframes once and hide whichever one contains
// the floating toggle DOM. The SDK ships without a stable className on the
// iframe element in some versions, so CSS alone isn't always enough.
const hideFloatingToggle = () => {
  const iframe = findFloatingToggleIframe();
  if (iframe && iframe.style.display !== 'none') {
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
  }
};

const ShopifyCartToggle = ({ className = '' }) => {
  const [count, setCount] = useState(0);
  const [hasCreds, setHasCreds] = useState(true);
  const initRef = useRef(false);

  useEffect(() => {
    let alive = true;
    getShopifySettings().then((s) => {
      if (alive) setHasCreds(!!(s.domain && s.token));
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    injectHideCSS();

    // Warm the SDK so it's ready by the time the user clicks a BUY button.
    getShopifyUI().catch(() => {});

    startPolling();

    const listener = (n) => setCount(n);
    _countListeners.add(listener);
    return () => { _countListeners.delete(listener); };
  }, []);

  const handleOpen = () => {
    if (!openCartViaToggle()) {
      // No items added yet → there's no floating toggle in the DOM yet.
      // Gently let the user know.
      // (Avoid alert() — just a no-op feels best; cart is empty anyway.)
    }
  };

  if (!hasCreds) return null;

  return (
    <button
      onClick={handleOpen}
      className={`relative p-2 text-[#D4AF37] hover:text-white transition-colors ${className}`}
      data-testid="shopify-cart-toggle"
      aria-label={`Cart (${count} items)`}
      title="Cart"
    >
      <ShoppingCart size={22} />
      {count > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-[#D4AF37] text-black text-xs font-bold rounded-full flex items-center justify-center"
          data-testid="shopify-cart-count"
        >
          {count}
        </span>
      )}
    </button>
  );
};

export default ShopifyCartToggle;
